/**
 * The secure viewer: watermarked page images, never the original file.
 *
 * What this is and is not, stated plainly because the distinction is the whole
 * design (and the SOP spec §2.3 agrees): it is DETERRENCE plus FORENSICS. Every
 * page carries who is looking and when, so a photograph of a screen still
 * carries an identity and the audit log says who had it open. It is not DRM,
 * and nothing here claims to stop a screenshot or a camera.
 *
 * The practical consequences:
 *
 * - the browser never receives the source file unless the caller holds the
 *   `download` right, in which case it is offered honestly as a download;
 * - `print` is offered only when the right is held — and even then the printed
 *   copy carries the same watermark, because it is the same image;
 * - pages load lazily, one request each, so a 300-page document does not become
 *   a 300-image payload.
 *
 * ACCESSIBILITY, stated rather than hidden: a canvas-rendered page image is not
 * screen-reader accessible. Where a reader is entitled to the content, the text
 * pane is the accessible alternative and is offered alongside. This is a real
 * tension between copy-protection and accessibility, not an oversight — the
 * repository's owner chooses which side to favour per kind of document.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { downloadAuthed, useAuthedSrc } from './authedSrc'
import type { DmsClient, DmsRights, DmsVersion } from './contracts'

export interface SecureDocViewerProps {
  client: DmsClient
  documentId: string
  version: DmsVersion | null
  rights: DmsRights
  title?: string
  /** Extracted text for the accessible alternative, when the host has it. */
  accessibleText?: string
  /**
   * Where a citation points: opened from a Q&A answer, the viewer scrolls to
   * the cited page and shows the cited passage in a callout. The pages are
   * watermarked IMAGES, so the passage cannot be painted onto the page
   * itself — the callout quoting it above the page is the honest equivalent.
   */
  focus?: { page?: number | null; excerpt?: string } | null
}

export function SecureDocViewer({
  client, documentId, version, rights, title, accessibleText, focus,
}: SecureDocViewerProps) {
  const pageCount = Math.max(1, Number(version?.page_count || 1))
  const [visible, setVisible] = useState(1)
  const [zoom, setZoom] = useState(1)
  const [showText, setShowText] = useState(false)
  const [failed, setFailed] = useState<Record<number, string>>({})
  const [showFocus, setShowFocus] = useState(true)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const focusScrolled = useRef(false)
  const focusPage = Math.min(pageCount, Math.max(0, Number(focus?.page || 0)))

  useEffect(() => {
    setVisible(1)
    setFailed({})
  }, [documentId, version?.id])

  // A citation names a page → render up to it (still lazily, one request per
  // page) and, once ITS image has painted, scroll it into view. The scroll
  // fires on the image's own load event rather than a timer, because the
  // pages above it change height as they load and a timed scroll lands wrong.
  useEffect(() => {
    focusScrolled.current = false
    setShowFocus(true)
    if (focusPage > 1) setVisible((n) => Math.max(n, focusPage))
  }, [focusPage, focus?.excerpt, documentId, version?.id])

  // Reveal the next page as the reader nears the bottom. Deliberately simple:
  // an IntersectionObserver per page would be more elegant and would also
  // prefetch pages nobody scrolled to, which is exactly what we do not want to
  // put on the audit log.
  const [printing, setPrinting] = useState(false)

  /**
   * Print the DOCUMENT, not the page it happens to be embedded in.
   *
   * `window.print()` prints the host page — app shell, sidebar, toolbar and all
   * — and only the pages lazily rendered so far, so a reader who had not
   * scrolled got page one of a forty-page SOP with a navigation bar across it.
   *
   * So the print job is built explicitly: an isolated iframe holding EVERY page
   * of this version, printed once its images have actually loaded. They are the
   * same server-rasterised images shown on screen, so the forensic watermark is
   * on every printed sheet — the print carries an identity exactly as the
   * screen does, and no raw file is involved.
   */
  const printDocument = useCallback(() => {
    if (printing) return
    setPrinting(true)
    const frame = document.createElement('iframe')
    // Off-screen rather than display:none — a hidden frame does not lay out,
    // and a frame that never laid out prints blank.
    frame.setAttribute('aria-hidden', 'true')
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;opacity:0;border:0;'
    document.body.appendChild(frame)

    const cleanup = () => {
      window.setTimeout(() => {
        frame.remove()
        setPrinting(false)
      }, 500)
    }

    try {
      const doc = frame.contentDocument
      if (!doc) throw new Error('no print document')
      const pages = Array.from({ length: pageCount }, (_, i) => i + 1)
      const safeTitle = (title || 'Document').replace(/[<>&]/g, ' ')
      doc.open()
      doc.write(
        '<!doctype html><html><head><title>' + safeTitle + '</title>'
        + '<style>'
        + '@page{margin:10mm}'
        + 'html,body{margin:0;padding:0;background:#fff}'
        + 'img{display:block;width:100%;page-break-after:always;break-after:page}'
        + 'img:last-child{page-break-after:auto;break-after:auto}'
        + '</style></head><body>'
        + pages.map((p) =>
          '<img src="' + client.pageUrl(documentId, p, version?.id) + '" alt="">').join('')
        + '</body></html>')
      doc.close()

      const win = frame.contentWindow
      if (!win) throw new Error('no print window')
      const start = () => {
        try {
          win.focus()
          win.print()
        } catch {
          // A blocked print is the browser's call, not an error worth a dialog.
        }
        cleanup()
      }
      // Wait for the images, but never indefinitely: one unrenderable page must
      // not mean the reader gets no print at all.
      const images = Array.from(doc.images)
      let pending = images.filter((img) => !img.complete).length
      if (!pending) {
        window.setTimeout(start, 50)
      } else {
        let done = false
        const finish = () => {
          if (done) return
          done = true
          start()
        }
        images.forEach((img) => {
          if (img.complete) return
          const tick = () => { pending -= 1; if (pending <= 0) finish() }
          img.addEventListener('load', tick, { once: true })
          img.addEventListener('error', tick, { once: true })
        })
        window.setTimeout(finish, 15000)
      }
    } catch {
      frame.remove()
      setPrinting(false)
      // Last resort: the old behaviour is worse, but silence is worse still.
      window.print()
    }
  }, [client, documentId, pageCount, printing, title, version?.id])

  const onScroll = useCallback(() => {
    const node = scrollRef.current
    if (!node) return
    const nearBottom = node.scrollTop + node.clientHeight >= node.scrollHeight - 400
    if (nearBottom) setVisible((n) => Math.min(pageCount, n + 1))
  }, [pageCount])

  if (!version) {
    return (
      <div style={emptyStyle}>
        This document has no file yet.
      </div>
    )
  }

  if (!rights.view) {
    return <div style={emptyStyle}>You do not have permission to view this document.</div>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={toolbarStyle}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{title || 'Document'}</span>
        <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>
          v{version.version_label} · {pageCount} page{pageCount === 1 ? '' : 's'}
        </span>
        <div style={{ flex: 1 }} />
        <button type="button" style={btnStyle} onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}>
          −
        </button>
        <span style={{ fontSize: 13, minWidth: 42, textAlign: 'center' }}>
          {Math.round(zoom * 100)}%
        </span>
        <button type="button" style={btnStyle} onClick={() => setZoom((z) => Math.min(2.5, z + 0.2))}>
          +
        </button>
        {accessibleText ? (
          <button
            type="button"
            style={btnStyle}
            aria-pressed={showText}
            onClick={() => setShowText((v) => !v)}
            title="Screen-reader accessible text of this document"
          >
            {showText ? 'Pages' : 'Text'}
          </button>
        ) : null}
        {rights.print ? (
          <button type="button" style={btnStyle} onClick={printDocument}
                  disabled={printing}
                  title="Prints every page of this document, watermarked">
            {printing ? 'Preparing…' : 'Print'}
          </button>
        ) : null}
        {rights.download ? (
          // A button, not a link: an <a href> is followed by the BROWSER, which
          // carries cookies and none of the headers a project user's session
          // is made of, so the download 404'd for them.
          <button
            type="button"
            style={btnStyle}
            onClick={() => { void downloadAuthed(
              client, client.downloadUrl(documentId, version.id),
              `${title || 'document'}.pdf`) }}
          >
            Download
          </button>
        ) : (
          <span style={{ ...btnStyle, opacity: 0.5, cursor: 'default' }} title="You do not have the download right for this document">
            Download
          </span>
        )}
      </div>

      {focus?.excerpt && showFocus ? (
        <div style={focusBarStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ fontSize: 13 }}>
              Cited passage{focusPage ? ` — page ${focusPage}` : ''}
            </strong>
            <div style={focusQuoteStyle}>“{focus.excerpt}”</div>
          </div>
          <button type="button" style={btnStyle} onClick={() => setShowFocus(false)}
                  aria-label="Dismiss cited passage">
            ×
          </button>
        </div>
      ) : null}

      {showText && accessibleText ? (
        <div style={textPaneStyle} role="document" aria-label={`${title || 'Document'} text`}>
          {accessibleText}
        </div>
      ) : (
        <div ref={scrollRef} onScroll={onScroll} style={pagesStyle}>
          {Array.from({ length: visible }, (_, index) => index + 1).map((page) => (
            // The figure carries the zoom, not the image: a percentage width
            // on the <img> was clamped by its own max-width, so zooming IN
            // moved the readout and nothing else.
            <figure
              key={page}
              data-page={page}
              style={{
                margin: 0, marginBottom: 16,
                width: `${Math.round(zoom * 100)}%`,
                marginLeft: 'auto', marginRight: 'auto',
              }}
            >
              {failed[page] ? (
                <div style={{ ...emptyStyle, padding: 24 }}>{failed[page]}</div>
              ) : (
                <PageImage
                  client={client}
                  url={client.pageUrl(documentId, page, version.id)}
                  alt={`${title || 'Document'} — page ${page}`}
                  onLoad={(event) => {
                    if (page !== focusPage || focusScrolled.current) return
                    focusScrolled.current = true
                    event.currentTarget.closest('figure')?.scrollIntoView(
                      { block: 'start', behavior: 'smooth' })
                  }}
                  onError={() => setFailed((prior) => ({
                    ...prior,
                    [page]: 'This page could not be rendered.',
                  }))}
                  style={{
                    width: '100%',
                    display: 'block',
                    border: '1px solid var(--color-border)',
                    outline: page === focusPage && showFocus
                      ? '2px solid var(--color-accent)' : undefined,
                    background: '#fff',
                    // Discourages the casual right-click-and-save. Honest about
                    // what it is: friction, not protection.
                    userSelect: 'none',
                  }}
                  draggable={false}
                />
              )}
              <figcaption style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                Page {page} of {pageCount}
              </figcaption>
            </figure>
          ))}
          {visible < pageCount ? (
            <button type="button" style={{ ...btnStyle, width: '100%' }} onClick={() => setVisible((n) => Math.min(pageCount, n + 3))}>
              Load more pages
            </button>
          ) : null}
        </div>
      )}
    </div>
  )
}

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  borderBottom: '1px solid var(--color-border)',
  flexWrap: 'wrap',
}

const btnStyle: React.CSSProperties = {
  padding: '4px 10px',
  fontSize: 13,
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  background: 'var(--color-bg-secondary)',
  color: 'var(--color-text)',
  cursor: 'pointer',
}

const pagesStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  padding: 16,
  background: 'var(--color-surface-2)',
}

const textPaneStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  padding: 24,
  whiteSpace: 'pre-wrap',
  fontSize: 15,
  lineHeight: 1.6,
  background: 'var(--color-bg-secondary)',
}

const emptyStyle: React.CSSProperties = {
  padding: 32,
  textAlign: 'center',
  color: 'var(--color-text-secondary)',
  fontSize: 14,
}

const focusBarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '8px 12px',
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-bg-secondary, rgba(49, 130, 206, 0.06))',
}

const focusQuoteStyle: React.CSSProperties = {
  fontSize: 13,
  color: 'var(--color-text-secondary)',
  fontStyle: 'italic',
  marginTop: 2,
  display: '-webkit-box',
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}

export default SecureDocViewer


/**
 * One page of the document.
 *
 * Its own component only so `useAuthedSrc` has somewhere to live — a hook
 * cannot be called inside the pages' `.map()`. It keeps the `<img>` exactly as
 * it was otherwise: the same events, the same styling, the same friction
 * against a casual right-click-and-save.
 */
function PageImage({ client, url, alt, onLoad, onError, style, draggable }: {
  client: DmsClient
  url: string
  alt: string
  onLoad?: React.ReactEventHandler<HTMLImageElement>
  onError?: React.ReactEventHandler<HTMLImageElement>
  style?: React.CSSProperties
  draggable?: boolean
}) {
  const src = useAuthedSrc(client, url)
  if (!src) {
    // The blob is in flight. A box of the right shape beats an <img> with no
    // src, which some browsers draw as a broken image.
    return <div style={{ ...style, minHeight: 120 }} aria-label={alt} />
  }
  return (
    <img src={src} alt={alt} onLoad={onLoad} onError={onError}
         style={style} draggable={draggable} />
  )
}
